import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Grid,
  Typography,
  Avatar,
  Box,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Skeleton,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { getUserProfileWithBadges } from '../services/apiservice';
import Timeline from '@mui/lab/Timeline';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';

const BadgePage = () => {
  const { profileId } = useParams();
  const [userProfile, setUserProfile] = useState(null);
  const [sortOrder, setSortOrder] = useState('Most Recent');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      const profileData = await getUserProfileWithBadges(profileId);
      if (profileData.success && profileData.message !== 'No user found with ProfileID') {
        setUserProfile(profileData.data);
      } else if (profileData.success && profileData.message == 'No user found with ProfileID') {
        navigate('*')
      }
    };

    fetchUserData();
  }, [profileId]);

  if (!userProfile) {
    return (
      <Container
        maxWidth={false}
        sx={{
          padding: '20px',
          background: 'linear-gradient(to right, #c3cfe2, #f5f7fa)',
          textAlign: 'center',
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100vh',
          overflowY: 'auto',
          zIndex: -1,
        }}
      >
        <Helmet>
          <title>CIRA - User Badge Profile</title>
        </Helmet>
        <Box
          textAlign="center"
          display="flex"
          flexDirection="column"
          alignItems="center"
        >
          <Skeleton variant="circular" width={120} height={120} />
          <Skeleton variant="text" height={60} width="25%" />
          <Skeleton variant="text" height={30} width="20%" />

          <Box
            display="flex"
            justifyContent="space-between"
            width="100%"
            mt={2}
            mb={3}
          >
            <Skeleton variant="text" height={40} width="15%" />
            <Skeleton variant="rectangular" width={120} height={40} />
          </Box>

          <Grid container spacing={2} justifyContent="center">
            {Array.from(new Array(6)).map((_, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box
                  sx={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '16px',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    textAlign: 'left',
                    height: '230px',
                    overflow: 'hidden',
                  }}
                >
                  <Skeleton variant="circle" width={80} height={80} sx={{ marginRight: '10px' }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Skeleton variant="text" width="60%" height={30} />
                    <Timeline>
                      <TimelineItem>
                        <TimelineSeparator>
                          <TimelineDot />
                          <TimelineConnector />
                        </TimelineSeparator>
                        <TimelineContent>
                          <Box>
                            <Skeleton variant="text" width="80%" />
                            <Skeleton variant="text" width="60%" />
                          </Box>
                        </TimelineContent>
                      </TimelineItem>
                      <TimelineItem>
                        <TimelineSeparator>
                          <TimelineDot />
                        </TimelineSeparator>
                        <TimelineContent>
                          <Box>
                            <Skeleton variant="text" width="60%" />
                            <Skeleton variant="rectangular" width={60} height={24} />
                          </Box>
                        </TimelineContent>
                      </TimelineItem>
                    </Timeline>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    );
  }

  const activeBadges = userProfile.badges.filter(badge => badge.AssignmentStatus === 'Active');
  const badgeCount = activeBadges.length;

  const sortedBadges = [...activeBadges].sort((a, b) => {
    if (sortOrder === 'Most Recent') {
      return new Date(b.AssignmentDate) - new Date(a.AssignmentDate);
    }
    return b.PopularityMetric - a.PopularityMetric;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
    return date.toLocaleDateString('en-GB', options).replace(/\//g, '-');
  };


  return (
    <Container
      maxWidth={false}
      sx={{
        padding: '20px',
        // background: 'linear-gradient(to right, #c3cfe2, #f5f7fa)',
        // backgroundImage: `url('https://cdn.svgator.com/images/2022/06/background-svg-image-pattern.svg')`,
        // backgroundImage: 'linear-gradient(to top, #6a85b6 0%, #bac8e0 100%)',
        backgroundImage: 'linear-gradient(to top, #a7a6cb 0%, #8989ba 52%, #8989ba 100%)',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        textAlign: 'center',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100vh',
        overflowY: 'auto',
        zIndex: -1,
      }}
    >

      <Box textAlign="center" marginBottom={4} sx={{ backgroundImage: 'linear-gradient(to top, #a7a6cb 0%, #8989ba 52%, #8989ba 100%)', padding: '16px', borderRadius: '8px' }}>
        <Avatar
          sx={{
            width: 120,
            height: 120,
            margin: '0 auto',
            border: '4px solid rgba(255, 255, 255, 0.8)',
            boxShadow: 2
          }}
          src={userProfile.ProfileImageUrl}
          alt={userProfile.name}
        />
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#fff' }}>
          {userProfile.name}
        </Typography>
        <Typography variant="subtitle1" color="textSecondary" sx={{ color: '#fff' }}>
          {userProfile.UserJobTitle}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <Typography variant="h6" sx={{ textAlign: 'left', color: '#333' }}>
          {badgeCount} Badge{badgeCount !== 1 ? 's' : ''} Issued
        </Typography>
        <FormControl variant="outlined" sx={{ minWidth: 120 }}>
          <InputLabel>Sort By</InputLabel>
          <Select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            label="Sort By"
          >
            <MenuItem value="Most Recent">Most Recent</MenuItem>
            <MenuItem value="Most Popular">Most Popular</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={2} justifyContent="center">
        {sortedBadges.map((badge, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '16px',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                background: 'linear-gradient(145deg, #f5f7fa, #c3cfe2)',
                display: 'flex',
                alignItems: 'center',
                textAlign: 'left',
                height: '230px',
                overflow: 'hidden',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-10px)',
                  boxShadow: '0 12px 24px rgba(0, 0, 0, 0.2)',
                  '& .credential-name': {
                    transform: 'scale(1.1)',
                  },
                },
              }}
            >
              <Avatar
                src={badge.CredentialImageURL}
                alt={badge.CredentialName}
                sx={{ width: 80, height: 80, marginRight: '10px', marginTop: '20px' }}
              />

              <Box sx={{ flexGrow: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 'bold',
                    marginTop: 5,
                    marginLeft: 0,
                    textAlign: 'left',
                    transition: 'transform 0.3s ease-in-out',
                  }}
                  className="credential-name"
                >
                  {badge.CredentialName}
                </Typography>
                <Timeline
                  sx={{
                    [`& .${timelineItemClasses.root}:before`]: {
                      flex: 0,
                      padding: 0,
                    },
                  }}
                >
                  <TimelineItem>
                    <TimelineSeparator>
                      <TimelineDot color="primary" />
                      <TimelineConnector />
                    </TimelineSeparator>
                    <TimelineContent>
                      <Box>
                        <Typography variant="caption" sx={{ color: 'black', display: 'block' }}>
                          Issued on
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#777', display: 'block' }}>
                          {formatDate(badge.AssignmentDate)}
                        </Typography>
                      </Box>
                    </TimelineContent>
                  </TimelineItem>
                  <TimelineItem>
                    <TimelineSeparator>
                      <TimelineDot color="primary" />
                    </TimelineSeparator>
                    <TimelineContent>
                      <Box>
                        <Typography variant="caption" sx={{ display: 'block', color: 'black' }}>
                          Status
                        </Typography>
                        <Chip
                          label={badge.AssignmentStatus}
                          size="small"
                          sx={{
                            backgroundColor: badge.AssignmentStatus === 'Active' ? 'green' : 'red',
                            color: 'white',
                            marginTop: '4px',
                          }}
                        />
                      </Box>
                    </TimelineContent>
                  </TimelineItem>
                </Timeline>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default BadgePage;
